import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Volume2, VolumeX } from 'lucide-react';

interface EmergencyAlertPayload {
  title?: string;
  body?: string;
  data?: {
    type?: string;
    alert_id?: string;
    property_id?: string;
    severity?: string;
    route?: string;
  };
}

export const EmergencyAlarmOverlay: React.FC = () => {
  const [activeAlert, setActiveAlert] = useState<EmergencyAlertPayload | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const vibrationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startSiren = useCallback(() => {
    try {
      const ctx = new AudioContext();
      audioContextRef.current = ctx;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();

      oscillatorRef.current = osc;
      gainRef.current = gain;

      // Siren sweep effect
      const sweep = () => {
        if (!audioContextRef.current || !oscillatorRef.current) return;
        const t = audioContextRef.current.currentTime;
        oscillatorRef.current.frequency.linearRampToValueAtTime(880, t + 0.5);
        oscillatorRef.current.frequency.linearRampToValueAtTime(440, t + 1.0);
      };
      sweep();
      const sirenInterval = setInterval(sweep, 1000);

      // Store interval for cleanup
      (audioContextRef.current as any).__sirenInterval = sirenInterval;
    } catch (e) {
      console.warn('Could not start siren audio:', e);
    }
  }, []);

  const stopSiren = useCallback(() => {
    try {
      if (oscillatorRef.current) {
        oscillatorRef.current.stop();
        oscillatorRef.current = null;
      }
      if (audioContextRef.current) {
        const interval = (audioContextRef.current as any).__sirenInterval;
        if (interval) clearInterval(interval);
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      gainRef.current = null;
    } catch (e) {
      // Ignore cleanup errors
    }
  }, []);

  const startVibration = useCallback(() => {
    if (!('vibrate' in navigator)) return;
    // Vibrate pattern: 500ms on, 200ms off
    vibrationIntervalRef.current = setInterval(() => {
      navigator.vibrate([500, 200, 500, 200, 500]);
    }, 2000);
    navigator.vibrate([500, 200, 500, 200, 500]);
  }, []);

  const stopVibration = useCallback(() => {
    if (vibrationIntervalRef.current) {
      clearInterval(vibrationIntervalRef.current);
      vibrationIntervalRef.current = null;
    }
    if ('vibrate' in navigator) {
      navigator.vibrate(0);
    }
  }, []);

  const acknowledge = useCallback(() => {
    stopSiren();
    stopVibration();
    setActiveAlert(null);
  }, [stopSiren, stopVibration]);

  const toggleMute = useCallback(() => {
    if (isMuted) {
      startSiren();
    } else {
      stopSiren();
    }
    setIsMuted(prev => !prev);
  }, [isMuted, startSiren, stopSiren]);

  // Listen for service worker messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'EMERGENCY_ALERT') {
        setActiveAlert(event.data.payload);
        startSiren();
        startVibration();
      }
      if (event.data?.type === 'EMERGENCY_ACKNOWLEDGED') {
        acknowledge();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
      stopSiren();
      stopVibration();
    };
  }, [startSiren, startVibration, stopSiren, stopVibration, acknowledge]);

  return (
    <AnimatePresence>
      {activeAlert && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
          style={{ background: 'rgba(0, 0, 0, 0.95)' }}
        >
          {/* Pulsing red background */}
          <motion.div
            className="absolute inset-0"
            animate={{
              backgroundColor: ['rgba(220, 38, 38, 0.3)', 'rgba(220, 38, 38, 0.7)', 'rgba(220, 38, 38, 0.3)'],
            }}
            transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center gap-6 px-6 text-center max-w-md">
            {/* Pulsing icon */}
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              <AlertTriangle className="w-24 h-24 text-red-500" strokeWidth={2.5} />
            </motion.div>

            <h1 className="text-3xl font-black text-white tracking-tight">
              🚨 EMERGENCY ALERT
            </h1>

            <h2 className="text-xl font-bold text-red-300">
              {activeAlert.title || 'Critical Alert'}
            </h2>

            <p className="text-lg text-gray-200">
              {activeAlert.body || 'An emergency has been reported. Please respond immediately.'}
            </p>

            {activeAlert.data?.route && (
              <p className="text-sm text-gray-400">
                Tap acknowledge to view details
              </p>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-3 w-full mt-4">
              <button
                onClick={() => {
                  acknowledge();
                  if (activeAlert.data?.route) {
                    window.location.href = activeAlert.data.route;
                  }
                }}
                className="w-full py-4 px-8 bg-red-600 hover:bg-red-700 text-white text-lg font-bold rounded-xl shadow-2xl active:scale-95 transition-transform"
              >
                ✓ ACKNOWLEDGE & VIEW
              </button>

              <div className="flex gap-3">
                <button
                  onClick={toggleMute}
                  className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  {isMuted ? 'Unmute' : 'Mute'}
                </button>

                <button
                  onClick={acknowledge}
                  className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  <X className="w-5 h-5" />
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
